import React from 'react';
import clsx from 'clsx';
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { Input, InputGroup } from '@oysterjs/ui/input';

import { useSearchExistingFilesQuery } from '~/api/graphql';
import type { PresetFileOption } from '~/components/workspaces/lib/convert-to-preset-wizard.utils';
import { buildAgencyDocumentStaticsUrl } from '~/components/workspaces/lib/artifact';
import { OfficeOnlineFilePreview } from '~/components/office-online-embed-url';

const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx'];

const fileExtension = (name: string): string =>
  name.includes('.') ? (name.split('.').pop() ?? '').toLowerCase() : '';

/**
 * Short badge for a tile. A searched file carries no mime type (SearchFileResult
 * exposes none), so the name's extension is the fallback, and FILE when there is
 * neither.
 */
export const fileKindLabel = (file: PresetFileOption): string => {
  const mimeType = file.mimeType ?? '';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.startsWith('image/')) return 'IMG';
  if (mimeType === 'text/csv') return 'CSV';
  if (mimeType.startsWith('text/')) return 'TXT';
  if (mimeType.includes('wordprocessingml') || mimeType === 'application/msword') return 'DOC';
  if (mimeType.includes('spreadsheetml') || mimeType === 'application/vnd.ms-excel') return 'XLS';

  const extension = fileExtension(file.name);
  return extension ? extension.slice(0, 4).toUpperCase() : 'FILE';
};

/**
 * Renderer for the preview panel. Word and Excel go through Microsoft's viewer,
 * which fetches the URL from its own servers — that works only because the statics
 * service serves agency documents without auth. Mime type is preferred over the
 * extension, since only the extension is available for a searched file.
 */
const FilePreviewBody = ({ file }: { file: PresetFileOption }) => {
  const mimeType = file.mimeType ?? '';
  const extension = fileExtension(file.name);
  const url = buildAgencyDocumentStaticsUrl(file.id);

  const isOffice =
    mimeType.includes('wordprocessingml') ||
    mimeType.includes('spreadsheetml') ||
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.ms-excel' ||
    (!mimeType && OFFICE_EXTENSIONS.includes(extension));
  if (isOffice) {
    return <OfficeOnlineFilePreview downloadUrl={url} iframeTitle={`Preview of ${file.name}`} />;
  }
  if (
    mimeType.startsWith('image/') ||
    (!mimeType && ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension))
  ) {
    return (
      <img src={url} alt={file.name} className="mx-auto max-h-full max-w-full object-contain" />
    );
  }
  // The browser renders PDFs and text itself; anything unrecognised is worth
  // attempting rather than refusing, since the header decides in the end.
  return <iframe src={url} title={`Preview of ${file.name}`} className="size-full border-0" />;
};

/** Fills the wizard's dialog body while open, so the step stays mounted behind it. */
export const PresetFilePreview = ({
  file,
  onBack
}: {
  file: PresetFileOption;
  onBack: () => void;
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex items-center justify-between gap-3">
      <p
        className="truncate text-sm/6 font-medium text-neutral-950 dark:text-white m-0"
        title={file.name}
      >
        {file.name}
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" plain href={buildAgencyDocumentStaticsUrl(file.id)} target="_blank">
          <ArrowTopRightOnSquareIcon className="size-4" aria-hidden />
          Open in new tab
        </Button>
        <Button type="button" color="white" onClick={onBack}>
          Back to files
        </Button>
      </div>
    </div>
    <div className="flex h-[60vh] items-center justify-center overflow-auto rounded-lg ring-1 ring-neutral-950/5 dark:ring-white/10">
      <FilePreviewBody file={file} />
    </div>
  </div>
);

const FileTile = ({
  file,
  selected,
  suggested,
  onToggle,
  onPreview
}: {
  file: PresetFileOption;
  selected: boolean;
  suggested: boolean;
  onToggle: () => void;
  onPreview: () => void;
}) => (
  <div
    onClick={onToggle}
    className={clsx(
      'flex aspect-square cursor-pointer flex-col rounded-lg p-2 transition-colors duration-150',
      selected
        ? 'bg-sky-50 ring-2 ring-sky-500 dark:bg-sky-500/10'
        : 'ring-1 ring-neutral-950/10 hover:bg-neutral-950/[2.5%] dark:ring-white/10 dark:hover:bg-white/[2.5%]'
    )}
  >
    <div className="flex items-start justify-between gap-1">
      {/* The tile toggles on click, so the checkbox and the preview button each
          stop propagation rather than firing the toggle twice. */}
      <span onClick={(e) => e.stopPropagation()}>
        <Checkbox
          color="sky"
          checked={selected}
          onChange={onToggle}
          aria-label={`Attach ${file.name}`}
        />
      </span>
      <div className="flex items-center gap-0.5">
        {suggested && (
          <SparklesIcon
            className="size-3.5 text-sky-500 dark:text-sky-400"
            aria-label="Suggested from this workspace"
          />
        )}
        <button
          type="button"
          aria-label={`Preview ${file.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onPreview();
          }}
          className="rounded-md p-0.5 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-950/5 hover:text-neutral-950 dark:hover:bg-white/10 dark:hover:text-white transition-colors duration-150"
        >
          <EyeIcon className="size-3.5" aria-hidden />
        </button>
      </div>
    </div>

    {/* flex-1 centers the badge in whatever the square leaves between the controls
        above and the name below. */}
    <div className="flex flex-1 items-center justify-center">
      <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-white/10 dark:text-neutral-300">
        {fileKindLabel(file)}
      </span>
    </div>

    <p
      className="line-clamp-2 break-words text-[11px] leading-tight text-neutral-700 dark:text-neutral-300 m-0"
      title={file.name}
    >
      {file.name}
    </p>
  </div>
);

/**
 * Step 3's default-file picker: the source workspace's files plus anything found by
 * search, all as tiles in one grid. One grid rather than a chip list beside it, so a
 * file looks and behaves the same however it was reached.
 */
export const PresetFilePicker = ({
  suggestedFiles,
  addedFiles,
  selectedIds,
  onAdd,
  onToggle,
  onPreview
}: {
  suggestedFiles: PresetFileOption[];
  addedFiles: PresetFileOption[];
  selectedIds: string[];
  onAdd: (file: PresetFileOption) => void;
  onToggle: (fileId: string) => void;
  onPreview: (file: PresetFileOption) => void;
}) => {
  const [query, setQuery] = React.useState('');
  const search = query.trim();

  const { data } = useSearchExistingFilesQuery({
    variables: { filters: { search } },
    skip: search.length === 0
  });

  const suggestedIds = React.useMemo(
    () => new Set(suggestedFiles.map((f) => f.id)),
    [suggestedFiles]
  );

  // Suggestions first, then what the user brought in, so the grid does not reorder
  // under them as they search.
  const tiles = React.useMemo(
    () => [...suggestedFiles, ...addedFiles.filter((f) => !suggestedIds.has(f.id))],
    [suggestedFiles, addedFiles, suggestedIds]
  );

  const results = (data?.searchFiles ?? []).slice(0, 8);

  return (
    <div data-slot="control" className="flex flex-col gap-2">
      <div className="relative">
        <InputGroup>
          <MagnifyingGlassIcon />
          <Input
            name="preset-file-search"
            aria-label="Search for existing files"
            placeholder="Search for existing files…"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
        </InputGroup>
        {search.length > 0 && results.length > 0 && (
          <div className="absolute inset-x-0 top-full z-10 mt-1 max-h-72 overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-neutral-950/5 dark:bg-neutral-800 dark:ring-white/10">
            {results.map((file) => {
              const attached = selectedIds.includes(file.id);
              return (
                <button
                  key={file.id}
                  type="button"
                  disabled={attached}
                  onClick={() => {
                    onAdd({ id: file.id, name: file.name });
                    setQuery('');
                  }}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm/6 text-neutral-700 enabled:hover:bg-neutral-950/5 disabled:text-neutral-400 dark:disabled:text-neutral-500 dark:text-neutral-300 dark:enabled:hover:bg-white/5"
                >
                  <span className="truncate">{file.name}</span>
                  {attached && (
                    <span className="flex shrink-0 items-center gap-1 text-xs">
                      <CheckIcon className="size-3.5" aria-hidden />
                      Attached
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {tiles.length > 0 && (
        <>
          <p className="text-sm/6 italic text-neutral-500 dark:text-neutral-400 m-0">
            Preview each file before you attach it.
          </p>
          {/* Twice the columns halves each tile's width, and aspect-square halves
              the height with it — a quarter of the area. */}
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {tiles.map((file) => (
              <FileTile
                key={file.id}
                file={file}
                selected={selectedIds.includes(file.id)}
                suggested={suggestedIds.has(file.id)}
                onToggle={() => onToggle(file.id)}
                onPreview={() => onPreview(file)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
